import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowUpRight, MapPin, Star, ShieldCheck, Clock } from "lucide-react";
import { MaskLines, Reveal, ClipImage } from "@/components/motion";
import { CTAButton, ArrowLink } from "@/components/CTA";
import Seo from "@/components/Seo";
import { CITIES, SERVICES, PRICING, BUSINESS } from "@/data";

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);
  if (!city) return <Navigate to="/" replace />;

  const others = CITIES.filter((c) => c.slug !== slug);

  return (
    <main data-testid={`city-page-${city.slug}`}>
      <Seo title={`${city.title} | Magical Cleaning Team`} description={city.metaDesc} path={`/${city.slug}`} />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 pt-36 pb-16 lg:px-10 lg:pt-48">
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="mb-8 inline-flex items-center gap-2 border border-[var(--line)] bg-[var(--brand-light)] px-4 py-2">
              <MapPin className="h-4 w-4 text-[var(--brand)]" />
              <span className="overline text-[var(--brand)]">{city.name}, Ontario</span>
            </div>
            <h1 className="font-display text-[3rem] font-extrabold leading-[0.92] tracking-tighter sm:text-6xl lg:text-[5rem]">
              <MaskLines lines={["Cleaning services", `in ${city.name}.`]} delay={0.1} lineClass="" />
            </h1>
            <Reveal delay={0.5} className="mt-8 max-w-xl text-lg leading-relaxed text-[var(--ink-soft)]">
              {city.intro}
            </Reveal>
            <Reveal delay={0.65} className="mt-10 flex flex-wrap items-center gap-4">
              <CTAButton to="/contact" testId={`city-quote-${city.slug}`}>Get Free Quote <ArrowUpRight className="h-4 w-4" /></CTAButton>
              <a href={`tel:${BUSINESS.phoneHref}`} data-testid={`city-call-${city.slug}`} className="nav-link text-sm font-semibold text-[var(--ink)]">
                Call {BUSINESS.phoneDisplay}
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <ClipImage src={city.image} alt={`House cleaning in ${city.name}`} className="aspect-[4/3] w-full" />
          </div>
        </div>
      </section>

      {/* Trust mini */}
      <section className="border-y border-[var(--line)]">
        <div className="mx-auto grid max-w-[1400px] gap-px bg-[var(--line)] sm:grid-cols-3">
          {[
            { icon: ShieldCheck, t: "Insured & vetted", d: "Background-checked local cleaners." },
            { icon: Clock, t: "Flexible scheduling", d: "Every day · 7am – 11pm." },
            { icon: Star, t: "4.9/5 rated", d: city.stat + "." },
          ].map((x, i) => (
            <Reveal key={x.t} delay={i * 0.08} className="bg-white p-8 lg:p-10">
              <x.icon className="h-7 w-7 text-[var(--brand)]" />
              <h3 className="mt-5 font-display text-xl font-bold tracking-tight">{x.t}</h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)]">{x.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services in city */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <Reveal className="max-w-2xl">
          <p className="overline text-[var(--brand)]">Our services in {city.name}</p>
          <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
            Every clean, covered in {city.name}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px bg-[var(--line)] sm:grid-cols-2">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06} className="bg-white p-8 lg:p-10">
              <span className="font-mono-accent text-sm text-[var(--brand)]">{s.num}</span>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight">{s.title} in {city.name}</h3>
              <p className="mt-3 leading-relaxed text-[var(--ink-soft)]">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Neighbourhoods */}
      <section className="border-t border-[var(--line)] bg-[var(--brand-light)]/40">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
          <Reveal>
            <p className="overline text-[var(--brand)]">Areas we cover</p>
            <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
              Proudly cleaning across {city.name}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-3">
            {city.neighbourhoods.map((n) => (
              <span key={n} className="border border-[var(--line)] bg-white px-4 py-2 text-sm font-medium">{n}</span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Testimonial + pricing */}
      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="flex gap-1 text-[var(--brand)]">
              {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-current" />)}
            </div>
            <p className="mt-6 font-display text-3xl font-medium leading-snug tracking-tight sm:text-4xl">
              “{city.testimonial.quote}”
            </p>
            <p className="mt-6 text-sm font-semibold">{city.testimonial.name} <span className="text-[var(--ink-soft)]">· {city.name}</span></p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
            <div className="border border-[var(--line)] p-8">
              <p className="overline text-[var(--brand)]">Starting from</p>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-6xl font-extrabold tracking-tighter">${PRICING[0].price}</span>
                <span className="mb-2 text-sm text-[var(--ink-soft)]">per visit</span>
              </div>
              <p className="mt-4 text-[var(--ink-soft)]">Affordable, upfront pricing for {city.name} homes. New customers save <span className="font-semibold text-[var(--brand)]">20%</span> on the first clean.</p>
              <CTAButton to="/contact" className="mt-8 w-full" testId={`city-book-${city.slug}`}>Book Now</CTAButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Other cities */}
      <section className="border-t border-[var(--line)]">
        <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10">
          <p className="overline text-[var(--brand)]">Also serving</p>
          <div className="mt-6 flex flex-wrap gap-6">
            {others.map((o) => (
              <ArrowLink key={o.slug} to={`/${o.slug}`} testId={`city-link-${o.slug}`}>Cleaning in {o.name}</ArrowLink>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[var(--ink)] text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-8 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10 lg:py-24">
          <h2 className="font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">Book your {city.name} clean today.</h2>
          <div className="flex flex-wrap gap-4">
            <CTAButton to="/contact" testId={`city-cta-quote-${city.slug}`}>Get Free Quote</CTAButton>
            <a href={`tel:${BUSINESS.phoneHref}`} data-testid={`city-cta-call-${city.slug}`} className="sweep-btn inline-flex items-center gap-2 border border-white/25 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5">
              <span className="relative z-10">Call Us Today</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
